import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ClipboardList, Download, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type IntakeStatus =
  | "new"
  | "contacted"
  | "in_review"
  | "scheduled"
  | "converted"
  | "declined";

const STATUSES: IntakeStatus[] = [
  "new",
  "contacted",
  "in_review",
  "scheduled",
  "converted",
  "declined",
];

type IntakeRow = {
  id: string;
  client_name: string;
  client_dob: string | null;
  guardian_name: string | null;
  guardian_relation: string | null;
  email: string;
  phone: string | null;
  city_state: string | null;
  best_contact_time: string | null;
  requested_services: string[];
  urgency: string | null;
  upcoming_deadlines: string | null;
  background_summary: string | null;
  disability_context: string | null;
  institutions: string[];
  key_concerns: string | null;
  documents_available: string[];
  risk_transportation: string | null;
  risk_behavior: string | null;
  risk_medical: string | null;
  income_context: string | null;
  hardship_description: string | null;
  has_caseworker: boolean;
  caseworker_contact: string | null;
  seeking_financial_help: string[];
  source: string | null;
  status: IntakeStatus;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
};

const STATUS_TONE: Record<IntakeStatus, string> = {
  new: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  contacted: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  in_review: "bg-primary/15 text-primary border-primary/30",
  scheduled: "bg-violet-500/15 text-violet-600 border-violet-500/30",
  converted: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  declined: "bg-destructive/15 text-destructive border-destructive/30",
};

const label = (s: string) => s.replace(/_/g, " ");

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const CSV_FIELDS: (keyof IntakeRow)[] = [
  "created_at",
  "status",
  "client_name",
  "email",
  "phone",
  "city_state",
  "best_contact_time",
  "urgency",
  "requested_services",
  "institutions",
  "documents_available",
  "seeking_financial_help",
  "upcoming_deadlines",
  "key_concerns",
  "disability_context",
  "background_summary",
  "has_caseworker",
  "caseworker_contact",
  "income_context",
  "hardship_description",
  "source",
  "reviewer_notes",
  "reviewed_at",
];

const csvCell = (value: unknown) => {
  const text = Array.isArray(value)
    ? value.join("; ")
    : value === null || value === undefined
      ? ""
      : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const exportCsv = (rows: IntakeRow[]) => {
  const csv = [
    CSV_FIELDS.join(","),
    ...rows.map((row) => CSV_FIELDS.map((field) => csvCell(row[field])).join(",")),
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `fsap-intakes-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const IntakeCard = ({
  row,
  selected,
  busy,
  onToggle,
  onSave,
}: {
  row: IntakeRow;
  selected: boolean;
  busy: boolean;
  onToggle: (checked: boolean) => void;
  onSave: (status: IntakeStatus, notes: string) => void;
}) => {
  const [status, setStatus] = useState<IntakeStatus>(row.status);
  const [notes, setNotes] = useState(row.reviewer_notes ?? "");
  const dirty = status !== row.status || notes !== (row.reviewer_notes ?? "");

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onToggle(v === true)}
            aria-label={`Select intake for ${row.client_name}`}
            className="mt-1"
          />
          <div>
            <CardTitle className="text-base">{row.client_name}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {[row.email, row.phone, row.city_state].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <Badge variant="outline" className={STATUS_TONE[row.status]}>
          {label(row.status)}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid gap-2 sm:grid-cols-3">
          {[
            { label: "Submitted", value: fmt(row.created_at) },
            { label: "Urgency", value: row.urgency ?? "—" },
            { label: "Best contact", value: row.best_contact_time ?? "—" },
            { label: "Guardian", value: row.guardian_name ?? "—" },
            { label: "Caseworker", value: row.has_caseworker ? row.caseworker_contact ?? "Yes" : "No" },
            { label: "Last reviewed", value: fmt(row.reviewed_at) },
          ].map((m) => (
            <div key={m.label}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{m.label}</div>
              <div>{m.value}</div>
            </div>
          ))}
        </div>

        {[
          { label: "Requested services", value: row.requested_services },
          { label: "Institutions", value: row.institutions },
          { label: "Documents available", value: row.documents_available },
          { label: "Financial help sought", value: row.seeking_financial_help },
        ]
          .filter((g) => g.value?.length)
          .map((g) => (
            <div key={g.label}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{g.label}</div>
              <div className="flex flex-wrap gap-1 pt-1">
                {g.value.map((v) => (
                  <Badge key={v} variant="secondary" className="font-normal">
                    {label(v)}
                  </Badge>
                ))}
              </div>
            </div>
          ))}

        {[
          { label: "Key concerns", value: row.key_concerns },
          { label: "Upcoming deadlines", value: row.upcoming_deadlines },
          { label: "Disability context", value: row.disability_context },
          { label: "Background", value: row.background_summary },
          { label: "Hardship", value: row.hardship_description },
        ]
          .filter((g) => g.value)
          .map((g) => (
            <div key={g.label}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{g.label}</div>
              <p className="whitespace-pre-wrap text-muted-foreground">{g.value}</p>
            </div>
          ))}

        <div className="grid gap-3 sm:grid-cols-[200px_1fr] sm:items-start">
          <Select value={status} onValueChange={(v) => setStatus(v as IntakeStatus)}>
            <SelectTrigger aria-label="Intake status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {label(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal follow-up notes"
            rows={2}
          />
        </div>

        <div className="flex justify-end">
          <Button size="sm" disabled={busy || !dirty} onClick={() => onSave(status, notes.trim())}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save update
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminIntakes = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<IntakeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | IntakeStatus>("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("fsap_intakes")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as unknown as IntakeRow[]) ?? []);
    setSelected(new Set());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return [r.client_name, r.email, r.city_state, r.guardian_name]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [rows, statusFilter, search]);

  const newCount = rows.filter((r) => r.status === "new").length;
  const selectedRows = visible.filter((r) => selected.has(r.id));

  const toggle = (id: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const save = async (row: IntakeRow, status: IntakeStatus, notes: string) => {
    setBusyId(row.id);
    const { error } = await supabase
      .from("fsap_intakes")
      .update({
        status,
        reviewer_notes: notes || null,
        reviewed_by: user?.id ?? null,
        reviewed_at: new Date().toISOString(),
      } as never)
      .eq("id", row.id);
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Intake marked ${label(status)}`);
    void load();
  };

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to review FSAP intake submissions.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <Helmet>
        <title>FSAP Intake Review | Admin</title>
        <meta
          name="description"
          content="Internal admin view to review FSAP intake submissions, update status, and export follow-up lists."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <ClipboardList className="h-5 w-5" /> FSAP intake review
          </h1>
          <p className="text-sm text-muted-foreground">
            {newCount} new {newCount === 1 ? "intake" : "intakes"} awaiting first contact.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={selectedRows.length === 0}
            onClick={() => exportCsv(selectedRows)}
          >
            <Download className="mr-2 h-4 w-4" />
            Export selected ({selectedRows.length})
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, or location"
          className="max-w-xs"
          aria-label="Search intakes"
        />
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-[180px]" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {label(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setSelected((prev) =>
              prev.size === visible.length ? new Set() : new Set(visible.map((r) => r.id)),
            )
          }
          disabled={visible.length === 0}
        >
          {selected.size === visible.length && visible.length > 0 ? "Clear selection" : "Select all"}
        </Button>
      </div>

      <div className="space-y-4">
        {visible.length === 0 && (
          <p className="text-sm text-muted-foreground">No intakes match this view.</p>
        )}
        {visible.map((row) => (
          <IntakeCard
            key={row.id}
            row={row}
            selected={selected.has(row.id)}
            busy={busyId === row.id}
            onToggle={(checked) => toggle(row.id, checked)}
            onSave={(status, notes) => void save(row, status, notes)}
          />
        ))}
      </div>
    </main>
  );
};

export default AdminIntakes;
